/**
 * Capability-based access control.
 *
 * The UI never branches on `role === 'ADMIN'`; it asks `can(user, 'course:create')`.
 * The API enforces the same map in `requirePermission()` middleware, so the two can
 * never drift. Adding a capability is a one-line change here plus a route guard.
 */
import { Role } from './enums';
export const Permission = {
    /* Learning content authoring */
    COURSE_VIEW: 'course:view',
    COURSE_CREATE: 'course:create',
    COURSE_UPDATE: 'course:update',
    COURSE_DELETE: 'course:delete',
    COURSE_PUBLISH: 'course:publish',
    /* Learner activity */
    LESSON_STUDY: 'lesson:study',
    LESSON_COMPLETE: 'lesson:complete',
    LESSON_REQUEST_SKIP: 'lesson:request-skip',
    QUIZ_ATTEMPT: 'quiz:attempt',
    EXERCISE_SUBMIT: 'exercise:submit',
    ASSESSMENT_ATTEMPT: 'assessment:attempt',
    /* Mentor review */
    SUBMISSION_VIEW_ALL: 'submission:view-all',
    SUBMISSION_REVIEW: 'submission:review',
    /**
     * Changing a decision that has already been given. Deliberately separate
     * from SUBMISSION_REVIEW and absent from MENTOR_PERMISSIONS: reviewing is a
     * mentor's job, but overturning a colleague's verdict — or your own, after
     * the intern has read it — is an oversight one.
     */
    SUBMISSION_REVIEW_AMEND: 'submission:review-amend',
    FEEDBACK_CREATE: 'feedback:create',
    /* Intern management */
    INTERN_VIEW: 'intern:view',
    INTERN_CREATE: 'intern:create',
    INTERN_UPDATE: 'intern:update',
    INTERN_DEACTIVATE: 'intern:deactivate',
    INTERN_OVERRIDE_PROGRESS: 'intern:override-progress',
    INTERN_SET_READINESS: 'intern:set-readiness',
    INTERN_GRADUATE: 'intern:graduate',
    SKILL_EVALUATE: 'skill:evaluate',
    /* Reporting & audit */
    REPORT_VIEW: 'report:view',
    REPORT_EXPORT: 'report:export',
    ANALYTICS_VIEW: 'analytics:view',
    ACTIVITY_LOG_VIEW: 'activity-log:view',
    AUDIT_LOG_VIEW: 'audit-log:view',
    /* Projects */
    PROJECT_VIEW: 'project:view',
    PROJECT_VIEW_ALL: 'project:view-all',
    PROJECT_MANAGE: 'project:manage',
    PROJECT_MEMBER_MANAGE: 'project:member-manage',
    TASK_MANAGE: 'task:manage',
    TASK_SUBMIT: 'task:submit',
    TASK_REVIEW: 'task:review',
    /* Business management */
    CLIENT_MANAGE: 'client:manage',
    FINANCE_VIEW: 'finance:view',
    FINANCE_MANAGE: 'finance:manage',
    INVOICE_MANAGE: 'invoice:manage',
    /* Platform administration */
    USER_MANAGE: 'user:manage',
    SETTINGS_MANAGE: 'settings:manage',
    CERTIFICATE_ISSUE: 'certificate:issue',
};
const P = Permission;
/** Everything an intern may do. Deliberately excludes all administrative capabilities. */
const INTERN_PERMISSIONS = [
    P.COURSE_VIEW,
    P.LESSON_STUDY,
    P.LESSON_COMPLETE,
    P.LESSON_REQUEST_SKIP,
    P.QUIZ_ATTEMPT,
    P.EXERCISE_SUBMIT,
    P.ASSESSMENT_ATTEMPT,
    P.PROJECT_VIEW,
    P.TASK_SUBMIT,
];
/** Mentors teach, review and manage interns, but do not touch money or platform settings. */
const MENTOR_PERMISSIONS = [
    P.COURSE_VIEW,
    P.COURSE_CREATE,
    P.COURSE_UPDATE,
    P.COURSE_DELETE,
    P.COURSE_PUBLISH,
    P.SUBMISSION_VIEW_ALL,
    P.SUBMISSION_REVIEW,
    P.FEEDBACK_CREATE,
    P.INTERN_VIEW,
    P.INTERN_CREATE,
    P.INTERN_UPDATE,
    P.INTERN_OVERRIDE_PROGRESS,
    P.INTERN_SET_READINESS,
    P.SKILL_EVALUATE,
    P.REPORT_VIEW,
    P.REPORT_EXPORT,
    P.ANALYTICS_VIEW,
    P.ACTIVITY_LOG_VIEW,
    P.PROJECT_VIEW,
    P.PROJECT_VIEW_ALL,
    P.PROJECT_MANAGE,
    P.PROJECT_MEMBER_MANAGE,
    P.TASK_MANAGE,
    P.TASK_REVIEW,
    P.CERTIFICATE_ISSUE,
];
/** Admins get every capability. */
const ADMIN_PERMISSIONS = Object.values(P);
export const ROLE_PERMISSIONS = {
    [Role.ADMIN]: ADMIN_PERMISSIONS,
    [Role.MENTOR]: MENTOR_PERMISSIONS,
    [Role.INTERN]: INTERN_PERMISSIONS,
};
/** True when `role` holds `permission`. */
export function roleHasPermission(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
/** True when `role` holds every listed permission. */
export function roleHasAllPermissions(role, permissions) {
    return permissions.every((permission) => roleHasPermission(role, permission));
}
/** True when `role` holds at least one of the listed permissions. */
export function roleHasAnyPermission(role, permissions) {
    return permissions.some((permission) => roleHasPermission(role, permission));
}
/** Staff (admin or mentor) see the administration area; interns never do. */
export function isStaffRole(role) {
    return role === Role.ADMIN || role === Role.MENTOR;
}
//# sourceMappingURL=permissions.js.map